/**
 * ============================================================================
 * VARIANT SELECTOR - Custom Web Component for Shopify Product Variants
 * ============================================================================
 *
 * This component handles product variant selection (colors, sizes, etc.) and
 * updates the page dynamically without a full reload.
 *
 * Supports multiple picker types:
 * - Radio buttons (swatch and pill/button styles)
 * - Dropdown select elements
 *
 * FLOW:
 * 1. User selects a variant option (radio button or dropdown)
 * 2. Component collects selected options
 * 3. Finds matching variant from product data
 * 4. Updates URL, form input, and page content
 */

class VariantSelector extends HTMLElement {
  /**
   * CONSTRUCTOR
   * ----------------------------------------------------------------------------
   * Called when the <variant-selector> element is created.
   *
   * - super() calls the parent HTMLElement constructor
   * - Binds the event handler to maintain proper 'this' context
   */
  constructor() {
    super();
    // Bind the handler once so we can properly remove it later
    this.handleChange = this.onVariantChange.bind(this);
  }

  /**
   * CONNECTED CALLBACK - Lifecycle Method
   * ----------------------------------------------------------------------------
   * Called automatically when the element is added to the DOM.
   * This is the proper place to attach event listeners.
   *
   * Web Components Best Practice:
   * - connectedCallback: Add listeners, initialize
   * - disconnectedCallback: Remove listeners, cleanup
   *
   * This ensures:
   * - No memory leaks
   * - Proper cleanup if element is removed and re-added
   * - Event listeners work after innerHTML replacements
   */
  connectedCallback() {
    this.addEventListener("change", this.handleChange);
    this.resetToFirstOptions();
  }

  /**
   * DISCONNECTED CALLBACK - Lifecycle Cleanup
   * ----------------------------------------------------------------------------
   * Called automatically when the element is removed from the DOM.
   * Removes event listeners to prevent memory leaks.
   *
   * Why this matters:
   * - If element is removed but listener stays, memory leak occurs
   * - Prevents duplicate listeners if element is re-added
   * - Good practice for all custom elements
   */
  disconnectedCallback() {
    this.removeEventListener("change", this.handleChange);
  }

  resetToFirstOptions() {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has("variant")) return;

  const groups = new Map();

  this.querySelectorAll('input[type="radio"]').forEach((radio) => {
    const name = radio.getAttribute("name");
    if (!name) return;
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(radio);
  });

  groups.forEach((radios) => {
    const serverDefault = radios.find((r) => r.hasAttribute("checked"));
    if (!serverDefault) return;

    const browserSelected = radios.find((r) => r.checked);
    if (browserSelected === serverDefault) return;

    radios.forEach((r) => (r.checked = false));
    serverDefault.checked = true;
  });

  window.addEventListener(
    "pageshow",
    (e) => {
      if (e.persisted) this.resetToFirstOptions();
    },
    { once: true }
  );
}

  /**
   * ON VARIANT CHANGE - Main Controller
   * ----------------------------------------------------------------------------
   * Triggered when user selects a different variant option.
   * Orchestrates the entire variant selection process.
   *
   * This works for both radio inputs and select dropdowns since both
   * trigger 'change' events that bubble up to the variant-selector element.
   *
   * Steps:
   * 1. Collect selected options (e.g., ["Red", "Large"])
   * 2. Find which variant matches those options
   * 3. Update browser URL with variant parameter
   * 4. Update hidden form input for cart submission
   * 5. Update media gallery to show variant image
   * 6. Fetch and update page content (price, button, etc.)
   * 7. Dispatch custom event for other components to react
   */
  onVariantChange(event) {
    this.updateOptions(); // Get selected options
    this.updateMasterId(); // Find matching variant

    if (!this.currentVariant) {
      console.log("No variant found");
      return;
    }

    console.log("Selected variant:", this.currentVariant);
    this.updateURL(); // Update browser URL
    this.updateVariantInput(); // Update hidden form input
    this.updateMedia(); // Update gallery to show variant image
    this.updateProductInfo(); // Fetch and update page content

    // Dispatch custom event for other components to listen to
    // Other components can listen: document.addEventListener('variant:updated', ...)
    this.dispatchVariantUpdatedEvent();
  }

  /**
   * UPDATE OPTIONS - Collect Selected Values
   * ----------------------------------------------------------------------------
   * Reads ALL radio/select inputs inside the <variant-selector> element in DOM
   * order, grouped by their unique name attribute, and reads the checked/selected
   * value for each group.
   *
   * This handles two name formats used in the theme:
   *   - Standard pickers:  name="OptionName-Position"  (e.g. "Throwing Hand-1")
   *   - Custom pickers:    name="options[OptionName]"  (e.g. "options[Web Type]")
   *
   * Reading in DOM order is critical — the Liquid template renders options in the
   * same sequence as Shopify's variant.options array, so DOM order == option order.
   *
   * Inputs are included when they satisfy at least one of:
   *   a. Inside a .product-form__input container  (standard pickers, color swatches)
   *   b. name starts with "options["              (glove web type pills)
   * All other inputs (e.g. properties[Position] hidden, position <select>) are skipped.
   */
  updateOptions() {
    const seen = new Map(); // option-name → checked value (insertion order = DOM order)

    // Iterate every radio/select inside THIS variant-selector element in document order.
    this.querySelectorAll('input[type="radio"], select').forEach((input) => {
      const name = input.getAttribute("name");
      if (!name) return;           // skip unnamed inputs (e.g. position dropdown)
      if (seen.has(name)) return;  // already processed this option group

      // Decide whether this input belongs to an option picker.
      const inOptionContainer = !!input.closest(".product-form__input");
      const isNamedOption     = name.startsWith("options[");
      if (!inOptionContainer && !isNamedOption) return; // skip non-option inputs

      if (input.tagName === "SELECT") {
        // Select elements: just read .value directly.
        seen.set(name, input.value);
        return;
      }

      // Radio group: find the currently checked radio for this name.
      // query inside variant-selector first (covers standard + glove web pills),
      // then fall back to a document-wide search for form-attribute-linked inputs.
      const escapedName = CSS.escape(name);
      const checked =
        this.querySelector(
          `input[type="radio"][name="${escapedName}"]:checked`,
        ) ||
        (() => {
          const anyInput = this.querySelector(`input[type="radio"][name="${escapedName}"]`);
          const fId = anyInput && anyInput.getAttribute("form");
          return fId
            ? document.querySelector(
                `input[type="radio"][form="${fId}"][name="${escapedName}"]:checked`,
              )
            : null;
        })();

      if (checked) seen.set(name, checked.value);
    });

    if (seen.size > 0) {
      this.options = Array.from(seen.values());
      console.log("Selected options:", this.options);
      return;
    }

    // ── Last-resort fallback (non-glove products with no options[] inputs) ──
    this.options = [];
    console.log("No options found — check input names in product form.");
  }

  /**
   * UPDATE MASTER ID - Find the Matching Variant
   * ----------------------------------------------------------------------------
   * Searches through all product variants to find one that matches
   * the currently selected options.
   *
   * How it works:
   * 1. Gets all variants from JSON (e.g., 20 variants total)
   * 2. For each variant, compares its options with selected options
   * 3. Returns the variant where ALL options match
   *
   * Example:
   * Available variants:
   *   { id: 123, options: ["Red", "Small"], price: "$20" }
   *   { id: 456, options: ["Red", "Large"], price: "$25" }  ← MATCH!
   *   { id: 789, options: ["Blue", "Large"], price: "$25" }
   *
   * Selected: ["Red", "Large"]
   * Result: this.currentVariant = { id: 456, options: ["Red", "Large"], ... }
   *
   * The logic:
   * - Maps each variant option to check if it equals selected option at same index
   * - If ANY option doesn't match, includes(false) returns true
   * - We use ! (NOT) to find variants where NO options are false (all match)
   */
  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  /**
   * UPDATE URL - Update Browser Address Bar
   * ----------------------------------------------------------------------------
   * Changes the URL to include the selected variant ID without reloading the page.
   *
   * Before: /products/t-shirt
   * After:  /products/t-shirt?variant=456
   *
   * Benefits:
   * - Users can bookmark/share specific variant URLs
   * - Back/forward buttons work correctly
   * - SEO friendly
   *
   * window.history.replaceState:
   * - First param: state object (empty here)
   * - Second param: title (empty, unused)
   * - Third param: new URL
   */
  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState(
      {},
      "",
      `${this.dataset.productUrl}?variant=${this.currentVariant.id}`,
    );
  }

  /**
   * UPDATE VARIANT INPUT - Update Form Hidden Input
   * ----------------------------------------------------------------------------
   * Updates the hidden input field inside the product form so when the form
   * submits, it adds the correct variant to the cart.
   *
   * Structure:
   * <form id="product-form-123" action="/cart/add">
   *   <input name="id" value="variant-id">  ← We update this
   *   <input name="quantity" value="1">
   * </form>
   *
   * <add-to-cart>
   *   <button form="product-form-123">Add to Cart</button>
   * </add-to-cart>
   *
   * Steps:
   * 1. Find the parent product form (variant-selector is inside it)
   * 2. Find the hidden input with name="id"
   * 3. Update its value to the new variant ID
   * 4. Dispatch change event (in case other scripts listen to it)
   *
   * Before: <input name="id" value="123">
   * After:  <input name="id" value="456">
   */
  updateVariantInput() {
    if (!this.currentVariant) return;

    // Find the product form - the variant-selector is inside it
    const productForm = this.closest('form[action*="/cart/add"]');

    if (!productForm) {
      console.warn("Product form not found");
      return;
    }

    // Find the hidden input directly in the form (not nested in add-to-cart)
    const variantInput = productForm.querySelector('input[name="id"]');

    if (variantInput) {
      variantInput.value = this.currentVariant.id;
      // Dispatch change event in case other scripts are listening
      variantInput.dispatchEvent(new Event("change", { bubbles: true }));
      console.log("Variant input updated to:", this.currentVariant.id);
    } else {
      console.warn('Variant input with name="id" not found in form');
    }
  }

  /**
   * UPDATE MEDIA - Switch Gallery to Variant Image
   * ----------------------------------------------------------------------------
   * When a variant is selected, this updates the Swiper gallery to show
   * the image associated with that variant (if it has one).
   *
   * Process:
   * 1. Check if variant has a featured_media (variant image)
   * 2. Find the media gallery element by ID
   * 3. Find the slide with matching media ID using data-media-id attribute
   * 4. Get the slide index
   * 5. Use Swiper's slideTo() to transition to that image
   * 6. Both main and thumbnail swipers update automatically (thumbs is linked)
   *
   * Example:
   * User selects "Red" variant → Gallery smoothly fades to red product image
   *
   * How it works with Swiper:
   * - Each slide has data-media-id="{{ media.id }}"
   * - Variant has featured_media.id that matches one of the slides
   * - We find the slide index and call slideTo(index)
   * - Swiper handles the smooth transition
   * - Thumbnail swiper syncs automatically via thumbs option
   */
  updateMedia() {
    // Exit if variant has no image
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const sectionId = this.dataset.sectionId;
    const mediaGallery = document.getElementById(`MediaGallery-${sectionId}`);
    if (!mediaGallery) return;

    // Find the slide that matches the variant's media ID
    const targetSlide = mediaGallery.querySelector(
      `.pdp-gallery-carousel__swiper--main [data-media-id="${this.currentVariant.featured_media.id}"]`,
    );
    if (!targetSlide) return;

    // Get all slides to find the index
    const allSlides = mediaGallery.querySelectorAll(
      ".pdp-gallery-carousel__swiper--main .swiper-slide",
    );
    const slideIndex = Array.from(allSlides).indexOf(targetSlide);

    if (slideIndex === -1) return;

    // Access the Swiper instance (stored globally with section ID)
    const swiperInstance = window[`swiperMain_${sectionId}`];
    if (swiperInstance) {
      // Slide to the variant image
      // The thumbnail swiper will automatically sync
      swiperInstance.slideTo(slideIndex);
      console.log(`Gallery updated to variant image at slide ${slideIndex}`);
    } else {
      console.warn("Swiper instance not found for section:", sectionId);
    }
  }

  /**
   * UPDATE PRODUCT INFO - Fetch & Replace Page Content
   * ----------------------------------------------------------------------------
   * Fetches the product page with the new variant and updates the UI.
   * This is what makes the page feel dynamic without a full reload.
   *
   * Process:
   * 1. Store the requested variant ID (to prevent race conditions)
   * 2. Fetch product page with ?variant=ID&section_id=SECTION parameters
   * 3. Parse the HTML response into a DOM object
   * 4. Extract the product info element by ID from the response
   * 5. Replace current product info content with new content
   * 6. Re-attach event listeners (because innerHTML replacement removes them)
   *
   * What gets updated:
   * - Price (sale price, compare at price)
   * - Product title
   * - Variant options (radio buttons/dropdowns with correct selections)
   * - Add to cart button (text and disabled state)
   * - Product description
   * - Any other content in the product info wrapper
   *
   * Race condition check:
   * If user quickly clicks Red → Blue → Green, we only want the final
   * selection (Green) to update the UI, not all three in sequence.
   */
  updateProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.sectionId;

    // ── Capture the user's current position selection BEFORE the fetch
    // replaces innerHTML, so we can restore it afterward if needed.
    const posSelectBefore = document.getElementById(
      `glove-position-select-${sectionId}`,
    );
    const savedPositionIdx = posSelectBefore ? posSelectBefore.value : null;

    fetch(
      `${this.dataset.productUrl}?variant=${requestedVariantId}&section_id=${sectionId}`,
    )
      .then((response) => response.text())
      .then((responseText) => {
        // Race condition check: make sure we're still on the same variant
        // (user might have clicked another option while fetching)
        if (this.currentVariant.id !== requestedVariantId) return;

        // Parse the HTML string into a DOM object we can query
        const html = new DOMParser().parseFromString(responseText, "text/html");

        // Extract the new product information section from the response using ID
        const newProductInfo = html.getElementById(`ProductInfo-${sectionId}`);
        const currentProductInfo = document.getElementById(
          `ProductInfo-${sectionId}`,
        );

        if (newProductInfo && currentProductInfo) {
          // Replace the entire content
          // This updates price, buttons, variant options, everything!
          currentProductInfo.innerHTML = newProductInfo.innerHTML;

          // ── CRITICAL: Re-execute inline <script> tags ──────────────────
          // Browsers do NOT automatically execute <script> elements that are
          // inserted via innerHTML — this is a browser security restriction.
          // Without this step, every custom component that registers its
          // listeners inside an inline <script> (like pdp-glove-web-selector)
          // loses ALL event listeners after the first variant update, making
          // the position dropdown and web pills completely unresponsive.
          //
          // We skip:
          //   • External scripts (src attribute) — already loaded once
          //   • JSON data blobs (type="application/json") — not executable JS
          currentProductInfo
            .querySelectorAll('script:not([src]):not([type="application/json"])')
            .forEach(function (oldScript) {
              const newScript = document.createElement("script");
              Array.from(oldScript.attributes).forEach(function (attr) {
                newScript.setAttribute(attr.name, attr.value);
              });
              newScript.textContent = oldScript.textContent;
              oldScript.parentNode.replaceChild(newScript, oldScript);
            });

          // ── Restore the user's position selection if it differs from the
          // server-rendered default (e.g. user picked "Outfield", a web pill
          // was auto-selected, the fetch returned — we want to keep Outfield
          // active rather than snapping back to what the server rendered).
          if (savedPositionIdx !== null) {
            const posSelectAfter = document.getElementById(
              `glove-position-select-${sectionId}`,
            );
            if (
              posSelectAfter &&
              posSelectAfter.value !== savedPositionIdx
            ) {
              posSelectAfter.value = savedPositionIdx;
              posSelectAfter.dispatchEvent(
                new Event("change", { bubbles: true }),
              );
            }
          }

          // IMPORTANT: Re-attach event listeners
          // When we replace innerHTML, all JavaScript event listeners are lost.
          // We need to find the new variant-selector element and add the
          // change listener back to it.
          const newVariantSelector =
            currentProductInfo.querySelector("variant-selector");
          if (newVariantSelector) {
            // The connectedCallback will automatically attach the listener
            // But if it doesn't trigger, we manually ensure it's attached
            if (!newVariantSelector.handleChange) {
              newVariantSelector.handleChange =
                this.onVariantChange.bind(newVariantSelector);
              newVariantSelector.addEventListener(
                "change",
                newVariantSelector.handleChange,
              );
            }
          }

          console.log("Product info updated");
        }
      })
      .catch((error) => {
        console.error("Error fetching product info:", error);
      });
  }

  /**
   * DISPATCH VARIANT UPDATED EVENT - Custom Event
   * ----------------------------------------------------------------------------
   * Dispatches a custom event that other components can listen to.
   * This allows other parts of your application to react to variant changes.
   *
   * Event details include:
   * - variantId: The selected variant's ID
   * - variant: The full variant object
   * - sectionId: The section ID for context
   *
   * Usage in other components:
   * ```javascript
   * document.addEventListener('variant:updated', (event) => {
   *   console.log('New variant selected:', event.detail.variantId);
   *   // Update other UI elements, analytics, etc.
   * });
   * ```
   *
   * Common use cases:
   * - Update related product recommendations
   * - Trigger analytics tracking
   * - Update size guides or fitting information
   * - Refresh dynamic pricing calculators
   * - Update quantity limits based on inventory
   */
  dispatchVariantUpdatedEvent() {
    const customEvent = new CustomEvent("variant:updated", {
      bubbles: true,
      detail: {
        variantId: this.currentVariant.id,
        variant: this.currentVariant,
        sectionId: this.dataset.sectionId,
      },
    });

    document.dispatchEvent(customEvent);
    console.log("Variant updated event dispatched:", this.currentVariant.id);
  }

  /**
   * GET VARIANT DATA - Load Product Variants from JSON
   * ----------------------------------------------------------------------------
   * Reads and caches the variant data from the JSON script tag.
   *
   * The Liquid template renders:
   * <script type="application/json">
   *   [
   *     { "id": 123, "options": ["Red", "Small"], "price": 2000, "available": true },
   *     { "id": 456, "options": ["Red", "Large"], "price": 2500, "available": true },
   *     ...
   *   ]
   * </script>
   *
   * This method:
   * 1. Checks if data is already cached (prevents re-parsing)
   * 2. Finds the script tag with type="application/json"
   * 3. Parses the JSON string into JavaScript array
   * 4. Caches it in this.variantData
   * 5. Returns the array
   *
   * Caching: Once parsed, we store it so subsequent calls don't
   * need to query the DOM and parse JSON again.
   */
  getVariantData() {
    // Return cached data if already loaded
    if (this.variantData) return this.variantData;

    // Find the JSON script tag
    const variantScript = this.querySelector('[type="application/json"]');
    if (!variantScript) {
      console.error("Variant data script not found");
      return [];
    }

    // Parse JSON and cache it
    this.variantData = JSON.parse(variantScript.textContent);
    return this.variantData;
  }
}

/**
 * REGISTER CUSTOM ELEMENT
 * ----------------------------------------------------------------------------
 * Tells the browser to use this VariantSelector class whenever it encounters
 * a <variant-selector> tag in the HTML.
 *
 * After this line, you can use:
 * <variant-selector data-product-url="/products/example">
 *   <!-- variant options here (radio or select) -->
 * </variant-selector>
 */
customElements.define("variant-selector", VariantSelector);
